import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import UpdateInfoModal from './UpdateInfoModal';
import SettingsColumnsModal from './SettingsColumnsModal';
import InsertLine from './InsertLine';
import * as XLSX from 'xlsx';

import DashboardSidebar from '../../../components/navigation/DashboardSidebar';
import DashboardHeader from '../../../components/navigation/DashboardHeader';
import DashboardFooter from '../../../components/navigation/DashboardFooter';
import MobileMenuButton from '../../../components/navigation/MobileMenuButton';


const InventoryTableView = () => {
  const { tableName } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [adminData, setAdminData] = useState({ nome: '', ruolo: '' });

  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [isAddingModalOpen, setIsAddingModalOpen] = useState(false);
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false); 
  const [selectedRow, setSelectedRow] = useState(null); 

  const [newRow, setNewRow] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnIsDropdown, setNewColumnIsDropdown] = useState(false);
  const [isProcessingColumn, setIsProcessingColumn] = useState(false);
  const [columnSettings, setColumnSettings] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState({});

  // NOVO: sortiranje
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // NOVO: rename headera
  const [editingHeader, setEditingHeader] = useState(null);
  const [headerEditValue, setHeaderEditValue] = useState('');


  useEffect(() => {
    fetchAdminData();
    fetchTableData();
  }, [tableName]);


  const fetchAdminData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profil } = await supabase.from('profili').select('nome, cognome, ruolo').eq('id', user.id).single();
        if (profil) setAdminData({ nome: `${profil.nome} ${profil.cognome}`, ruolo: profil.ruolo });
      }
    } catch (err) { console.error(err.message); }
  };


  const fetchTableData = async () => {
    setLoading(true);
    try {
      const { data: rows, error } = await supabase.from(tableName).select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setData(rows);

      const { data: meta } = await supabase
        .from('inventura_registar')
        .select('postavke_kolona')
        .eq('naziv_tabele_sql', tableName)
        .single();
      
      const settings = meta?.postavke_kolona || {};
      setColumnSettings(settings);

      const excluded = ['id', 'id_azienda', 'id_utente', 'created_at'];
      const sampleRow = rows[0] || {};
      const dynamicCols = Object.keys(sampleRow).filter(col => !excluded.includes(col));
      setColumns(dynamicCols);

      const optionsMap = {};
      for (const col of dynamicCols) {
        if (settings[col]?.type === 'dropdown') {
          const { data: uniqueVals } = await supabase.rpc('get_unique_values', {
            p_table_name: tableName,
            p_column_name: col
          });
          if (uniqueVals) optionsMap[col] = uniqueVals.map(v => v.vrijednost);
        }
      }
      setDropdownOptions(optionsMap);
    } catch (err) { console.error(err.message); } finally { setLoading(false); }
  };


  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(sortedData.map(row => {
      const r = {};
      columns.forEach(col => { r[col.replace(/_/g, ' ').toUpperCase()] = row[col]; });
      return r;
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventar");
    XLSX.writeFile(wb, `${tableName}_export.xlsx`);
  };


  const handleAddColumn = async (colType) => { 
    if (!newColumnName || !tableName) return;
    setIsProcessingColumn(true);
    try {
      const cleanName = newColumnName.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      const typeMap = { text: 'TEXT', number: 'NUMERIC', date: 'DATE', boolean: 'BOOLEAN' };
      const sqlTypeName = typeMap[colType] || 'TEXT';

      const { error: dbError } = await supabase.rpc('add_column_to_inventory_table', {
        p_table_name: tableName,
        p_column_name: cleanName,
        p_type: sqlTypeName
      });
      if (dbError) throw dbError;

      if (newColumnIsDropdown) {
        const { data: regData } = await supabase.from('inventura_registar').select('postavke_kolona').eq('naziv_tabele_sql', tableName).single();
        let currentSettings = regData?.postavke_kolona || {};
        currentSettings[cleanName] = { type: 'dropdown' };
        await supabase.from('inventura_registar').update({ postavke_kolona: currentSettings }).eq('naziv_tabele_sql', tableName);
      }

      setNewColumnName('');
      setNewColumnIsDropdown(false);
      await fetchTableData(); 
      setIsColumnModalOpen(false);
    } catch (err) { alert(t('inventory_table.error_prefix') + err.message); } finally { setIsProcessingColumn(false); }
  };


  const handleInlineUpdate = async () => {
    if (!editingCell) return;
    try {
      const { error } = await supabase.from(tableName).update({ [editingCell.column]: editValue }).eq('id', editingCell.id);
      if (error) throw error;
      setData(data.map(item => item.id === editingCell.id ? { ...item, [editingCell.column]: editValue } : item));
      setEditingCell(null);
    } catch (err) { alert(err.message); }
  };


  const handleUpdateSave = async (updatedRow) => {
    try {
      const { error } = await supabase.from(tableName).update(updatedRow).eq('id', updatedRow.id);
      if (error) throw error;
      setData(data.map(item => item.id === updatedRow.id ? updatedRow : item));
      setIsInfoModalOpen(false);
    } catch (err) { alert(err.message); }
  };


  const handleUpdate = (row) => { setSelectedRow(row); setIsInfoModalOpen(true); };


  const toggleColumnDropdown = async (colName) => {
    try {
      const { data: regData } = await supabase.from('inventura_registar').select('postavke_kolona').eq('naziv_tabele_sql', tableName).single();
      let currentSettings = regData?.postavke_kolona || {};
      if (currentSettings[colName]?.type === 'dropdown') delete currentSettings[colName];
      else currentSettings[colName] = { type: 'dropdown' };
      await supabase.from('inventura_registar').update({ postavke_kolona: currentSettings }).eq('naziv_tabele_sql', tableName);
      setColumnSettings(currentSettings);
      fetchTableData();
    } catch (err) { alert(err.message); }
  };


  const handleDropColumn = async (colName) => {
    if (!window.confirm(t('inventory_table.confirm_delete_column', { name: colName }))) return;
    setIsProcessingColumn(true);
    try {
      await supabase.rpc('drop_column_from_table', { t_name: tableName, c_name: colName });
      fetchTableData();
    } catch (err) { alert(err.message); } finally { setIsProcessingColumn(false); }
  };


  const handleInsert = async (validatedData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: profil } = await supabase.from('profili').select('id_azienda').eq('id', user.id).single();
      const payload = { ...validatedData, id_azienda: profil.id_azienda, id_utente: user.id };
      await supabase.from(tableName).insert([payload]);
      setIsAddingModalOpen(false);
      setNewRow({});
      fetchTableData();
    } catch (err) { alert(err.message); }
  };


  const handleDelete = async (id) => {
    if (!window.confirm(t('inventory_table.confirm_delete_row'))) return;
    setDeletingId(id);
    try {
      await supabase.from(tableName).delete().eq('id', id);
      setData(data.filter(item => item.id !== id));
    } catch (err) { alert(err.message); } finally { setDeletingId(null); }
  };


  // filteredData MORA biti prije sortedData
  const filteredData = data.filter(row => 
    columns.some(col => String(row[col] || '').toLowerCase().includes(searchTerm.toLowerCase()))
  );


  // NOVO: detekcija tipa kolone
  const detectColumnType = (col) => {
    const firstVal = filteredData.find(r => r[col] !== null && r[col] !== undefined)?.[col];
    if (firstVal === undefined) return 'text';
    if (typeof firstVal === 'boolean') return 'boolean';
    if (typeof firstVal === 'number') return 'number';
    if (typeof firstVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(firstVal)) return 'date';
    return 'text';
  };


  // NOVO: sortiranje
  const handleSort = (col) => {
    setSortConfig(prev => ({
      key: col,
      direction: prev.key === col && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    const type = detectColumnType(sortConfig.key);
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      let result = 0;
      if (type === 'boolean') result = (aVal === bVal ? 0 : aVal ? 1 : -1);
      else if (type === 'number') result = (parseFloat(aVal) || 0) - (parseFloat(bVal) || 0);
      else if (type === 'date') result = new Date(aVal || 0) - new Date(bVal || 0);
      else result = String(aVal || '').toLowerCase().localeCompare(String(bVal || '').toLowerCase());
      return sortConfig.direction === 'asc' ? result : -result;
    });
  }, [filteredData, sortConfig]);


  // NOVO: rename headera - dvoklik
  const handleHeaderDoubleClick = (col) => {
    setEditingHeader(col);
    setHeaderEditValue(col.replace(/_/g, ' '));
  };

  const handleHeaderRename = async () => {
    if (!editingHeader) return;
    const oldName = editingHeader;
    const newName = headerEditValue.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    setEditingHeader(null);
    if (!newName || oldName === newName) return;
    try {
      await supabase.rpc('rename_column', { t_name: tableName, old_name: oldName, new_name: newName });
      if (columnSettings[oldName]) {
        const updated = { ...columnSettings, [newName]: columnSettings[oldName] };
        delete updated[oldName];
        await supabase.from('inventura_registar').update({ postavke_kolona: updated }).eq('naziv_tabele_sql', tableName);
      }
      await fetchTableData();
    } catch (err) { alert(t('inventory_table.error_prefix') + err.message); }
  };


  const renderCellContent = (row, col) => {
    const val = row[col];
    if (typeof val === 'boolean') {
      return (
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[10px] font-black uppercase tracking-widest ${val ? 'bg-primary/10 border-primary/20 text-primary' : 'bg-muted border-border/60 text-muted-foreground'}`}>
          <div className={`w-1.5 h-1.5 rounded-full ${val ? 'bg-primary' : 'bg-muted-foreground/40'}`} />
          {val ? t('inventory_table.status_true') : t('inventory_table.status_false')}
        </div>
      );
    }
    return <span className="text-sm font-bold text-foreground/80 tracking-tight">{val || <span className="opacity-20">—</span>}</span>;
  };


  return (
    <div className="min-h-screen bg-background text-foreground">
      <MobileMenuButton isOpen={isSidebarOpen} onClick={() => setIsSidebarOpen(!isSidebarOpen)} />
      <DashboardSidebar isCollapsed={!isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} />
      <DashboardHeader onLogout={() => navigate('/landing-authentication')} userName={adminData.nome || 'Admin'} />

      <main className="main-content with-sidebar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10 animate-in fade-in duration-500">
          
          <div className="flex justify-start mb-6">
            <button onClick={() => navigate(-1)} className="group flex items-center gap-2 text-muted-foreground hover:text-primary transition-all">
              <div className="p-2 rounded-xl bg-muted/30 group-hover:bg-primary/10 transition-colors">
                <Icon name="ArrowLeft" size={20} className="group-hover:-translate-x-1 transition-transform" />
              </div>
              <span className="text-[11px] font-black uppercase tracking-[0.2em]">{t('inventory_table.back')}</span>
            </button>
          </div>

          <div className="relative mb-10 flex flex-col xl:flex-row xl:items-end justify-between gap-6">
            <div className="flex flex-col relative z-10 bg-background pr-4 w-fit">
              <h2 className="text-4xl font-black uppercase tracking-tighter flex items-center gap-4 text-foreground/90">
                <div className="p-3 bg-primary rounded-2xl text-white shadow-xl shadow-primary/20">
                  <Icon name="Database" size={28} />
                </div>
                {tableName ? tableName.replace('inv_', '').replace(/_/g, ' ') : t('inventory_table.table_default_name')}
              </h2>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-4 w-full xl:w-auto">
              <div className="relative w-full md:w-80 group">
                <Icon name="Search" size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground/50" />
                <input 
                  type="text"
                  placeholder={t('inventory_table.search_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-card border border-border/60 rounded-2xl pl-12 pr-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10 transition-all"
                />
              </div>

              <button 
                onClick={exportToExcel} 
                title={t('inventory_table.export_excel')}
                className="p-4 bg-emerald-500/10 text-emerald-600 rounded-2xl hover:bg-emerald-500 hover:text-white transition-all border border-emerald-500/20 shadow-lg shadow-emerald-500/10"
              >
                <Icon name="FileSpreadsheet" size={20} />
              </button>

              <button 
                onClick={() => setIsColumnModalOpen(true)} 
                title={t('inventory_table.settings_title')}
                className="p-4 bg-muted/50 text-muted-foreground rounded-2xl hover:bg-primary/10 hover:text-primary transition-all border border-border/40"
              >
                <Icon name="Settings" size={20} />
              </button>

              <button 
                onClick={() => setIsAddingModalOpen(true)} 
                title={t('inventory_table.add_row_title')}
                className="group flex items-center gap-3 px-8 py-4 rounded-2xl bg-foreground text-background transition-all shadow-2xl hover:scale-[1.02] active:scale-[0.98]"
              >
                <Icon name="Plus" size={20} />
                <span className="text-xs font-black uppercase tracking-[0.2em]">{t('inventory_table.btn_new_entry')}</span>
              </button>
            </div>
          </div>

          <div className="bg-card rounded-[2.5rem] border border-border/60 shadow-2xl overflow-hidden flex flex-col transition-all">
            <div className="overflow-auto max-h-[70vh] custom-scrollbar">
              <table className="w-full text-left border-collapse table-auto">
                <thead className="sticky top-0 z-20 bg-muted/95 backdrop-blur-md">
                  <tr className="border-b border-border/50">
                    {columns.map(col => (
                      <th
                        key={col}
                        className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 whitespace-nowrap w-px cursor-pointer select-none hover:text-primary/80 transition-colors"
                        onClick={() => { if (!editingHeader) handleSort(col); }}
                        onDoubleClick={() => handleHeaderDoubleClick(col)}
                      >
                        {editingHeader === col ? (
                          <input
                            autoFocus
                            className="min-w-[120px] bg-background border-2 border-primary/40 rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-foreground outline-none shadow-inner"
                            value={headerEditValue}
                            onChange={(e) => setHeaderEditValue(e.target.value)}
                            onBlur={handleHeaderRename}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleHeaderRename(); if (e.key === 'Escape') setEditingHeader(null); }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            {col.replace(/_/g, ' ')}
                            {columnSettings[col]?.type === 'dropdown' && <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />}
                            {sortConfig.key === col && (
                              <Icon name={sortConfig.direction === 'asc' ? 'ArrowUp' : 'ArrowDown'} size={12} className="text-primary shrink-0" />
                            )}
                          </div>
                        )}
                      </th>
                    ))}
                    <th className="px-8 py-6 text-center w-20 sticky right-0 bg-muted/95 backdrop-blur-md border-l border-border/10">{t('inventory_table.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/30">
                  {loading ? (
                    <tr><td colSpan={columns.length + 1} className="py-40 text-center"><div className="flex flex-col items-center gap-4"><div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" /><span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{t('inventory_table.loading')}</span></div></td></tr>
                  ) : sortedData.map((row) => (
                    <tr key={row.id} className="hover:bg-primary/[0.04] transition-colors group cursor-default">
                      {columns.map(col => (
                        <td key={col} className="px-8 py-5 whitespace-nowrap" onDoubleClick={() => { setEditingCell({ id: row.id, column: col }); setEditValue(row[col] || ''); }}>
                          {editingCell?.id === row.id && editingCell?.column === col ? (
                            <input 
                              autoFocus className="min-w-[150px] bg-background border-2 border-primary/40 rounded-xl px-3 py-2 text-sm font-bold outline-none shadow-inner"
                              value={editValue} onChange={(e) => setEditValue(e.target.value)}
                              onBlur={handleInlineUpdate} onKeyDown={(e) => e.key === 'Enter' && handleInlineUpdate()}
                            />
                          ) : (
                            renderCellContent(row, col)
                          )}
                        </td>
                      ))}
                      <td className="px-8 py-5 text-center sticky right-0 bg-card/80 backdrop-blur-sm group-hover:bg-primary/[0.01] transition-colors border-l border-border/10">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => handleUpdate(row)} className="p-2.5 text-muted-foreground/30 hover:text-primary hover:bg-primary/10 rounded-xl transition-all">
                            <Icon name="Eye" size={18} />
                          </button>
                          <button onClick={() => handleDelete(row.id)} className="p-2.5 text-muted-foreground/30 hover:text-destructive hover:bg-destructive/10 rounded-xl transition-all">
                            {deletingId === row.id ? <div className="w-4 h-4 border-2 border-t-transparent border-destructive rounded-full animate-spin" /> : <Icon name="Trash2" size={18} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      <UpdateInfoModal 
        isOpen={isInfoModalOpen} 
        onClose={() => setIsInfoModalOpen(false)} 
        selectedRow={selectedRow}
        columns={columns}
        onSave={handleUpdateSave}
      />

      <SettingsColumnsModal 
        isOpen={isColumnModalOpen}
        onClose={() => setIsColumnModalOpen(false)}
        newColumnName={newColumnName}
        setNewColumnName={setNewColumnName}
        newColumnIsDropdown={newColumnIsDropdown}
        setNewColumnIsDropdown={setNewColumnIsDropdown}
        handleAddColumn={handleAddColumn} 
        columns={columns}
        columnSettings={columnSettings}
        toggleColumnDropdown={toggleColumnDropdown}
        handleDropColumn={handleDropColumn}
      />

      <InsertLine 
        isOpen={isAddingModalOpen}
        onClose={() => setIsAddingModalOpen(false)}
        tableName={tableName}
        columns={columns}
        columnSettings={columnSettings}
        dropdownOptions={dropdownOptions}
        newRow={newRow}
        setNewRow={setNewRow}
        handleInsert={handleInsert}
      />

      <DashboardFooter />
    </div>
  );
};

export default InventoryTableView;